import React, { useEffect, useState } from 'react';
import { adminMealsAPI } from '../../services/meals.api';
import { Link as RouterLink } from 'react-router-dom';

// Material UI imports
import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  CardActions,
  Button,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Avatar,
  IconButton,
  Tooltip,
  Chip,
  LinearProgress,
  Skeleton,
  useTheme,
  alpha
} from '@mui/material';

// Icons
import {
  Dashboard as DashboardIcon,
  RestaurantMenu,
  Fastfood,
  TrendingUp,
  Language,
  People,
  AccessTime,
  LocalDining,
  Cake,
  Spa,
  FoodBank,
  EmojiFoodBeverage,
  Add,
  Settings,
  BarChart,
  PieChart,
  DonutLarge,
  Tune as TuneOutlined
} from '@mui/icons-material';
import NotificationsActive from '@mui/icons-material/NotificationsActive';


const Dashboard = () => {
  const theme = useTheme();
  const [stats, setStats] = useState({
    totalMeals: 0,
    mealsByType: {},
    mealsByDiet: {},
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await adminMealsAPI.getAllMeals();
        const meals = response.data;

        // Calculate statistics
        const mealsByType = meals.reduce((acc, meal) => {
          acc[meal.mealType] = (acc[meal.mealType] || 0) + 1;
          return acc;
        }, {});

        const mealsByDiet = meals.reduce((acc, meal) => {
          meal.dietaryType.forEach(type => {
            acc[type] = (acc[type] || 0) + 1;
          });
          return acc;
        }, {});

        setStats({
          totalMeals: meals.length,
          mealsByType,
          mealsByDiet,
        });
      } catch (error) {
        console.error('Error fetching stats:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  // Helper functions for data visualization
  const getPercentage = (count, total) => {
    return total > 0 ? Math.round((count / total) * 100) : 0;
  };
  
  const getColorForType = (type) => {
    const colorMap = {
      breakfast: '#FF9800', // orange
      lunch: '#2196F3', // blue
      dinner: '#673AB7', // deep purple
      veg: '#4CAF50', // green
      'non-veg': '#F44336', // red
      jain: '#9C27B0', // purple
      healthy: '#8BC34A', // light green
      spicy: '#FF5722', // deep orange
      sweet: '#E91E63', // pink
      'default': '#607D8B' // blue grey
    };
    return colorMap[type.toLowerCase()] || colorMap['default'];
  };
  
  const getMealTypeIcon = (type) => {
    switch(type.toLowerCase()) {
      case 'breakfast':
        return <AccessTime />;
      case 'lunch':
        return <LocalDining />;
      case 'dinner':
        return <FoodBank />;
      default:
        return <RestaurantMenu />;
    }
  };
  
  const getDietIcon = (diet) => {
    switch(diet.toLowerCase()) {
      case 'veg':
        return <Spa />;
      case 'non-veg':
        return <Fastfood />;
      case 'jain':
        return <EmojiFoodBeverage />;
      case 'healthy':
        return <LocalDining />;
      case 'dessert':
        return <Cake />;
      default:
        return <RestaurantMenu />;
    }
  };

  // For loading state
  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Skeleton variant="rectangular" height={100} />
          </Grid>
          <Grid item xs={12} md={4}>
            <Skeleton variant="rectangular" height={240} />
          </Grid>
          <Grid item xs={12} md={4}>
            <Skeleton variant="rectangular" height={240} />
          </Grid>
          <Grid item xs={12} md={4}>
            <Skeleton variant="rectangular" height={240} />
          </Grid>
          <Grid item xs={12}>
            <Skeleton variant="rectangular" height={150} />
          </Grid>
        </Grid>
      </Container>
    );
  }

  // Calculate additional stats for visualization
  const mealTypesWithPercentage = Object.entries(stats.mealsByType).map(([type, count]) => ({
    type,
    count,
    percentage: getPercentage(count, stats.totalMeals)
  })).sort((a, b) => b.count - a.count);
  
  const dietTypesWithPercentage = Object.entries(stats.mealsByDiet).map(([diet, count]) => ({
    diet,
    count,
    percentage: getPercentage(count, stats.totalMeals)
  })).sort((a, b) => b.count - a.count);
  
  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Welcome Header */}
      <Box mb={4}>
        <Typography variant="h3" component="h1" fontWeight="bold" color="primary" gutterBottom>
          Admin Dashboard
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          Manage your meal suggestions platform and view analytics
        </Typography>
      </Box>
      
      {/* Stats Overview */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} md={4}>
          <Paper
            elevation={3}
            sx={{
              p: 3,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              height: 180,
              justifyContent: 'center',
              borderRadius: 2,
              position: 'relative',
              overflow: 'hidden',
              background: (theme) => `linear-gradient(45deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
              color: 'white'
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                top: -30,
                right: -20,
                opacity: 0.2,
                transform: 'rotate(15deg)'
              }}
            >
              <RestaurantMenu sx={{ fontSize: 160 }} />
            </Box>
            <Box sx={{ textAlign: 'center', zIndex: 2 }}>
              <Typography variant="h2" component="div" fontWeight="bold">
                {stats.totalMeals}
              </Typography>
              <Typography variant="h6" sx={{ mt: 1 }}>
                Total Meals
              </Typography>
            </Box>
          </Paper>
        </Grid>
        
        <Grid item xs={12} md={4}>
          <Paper
            elevation={3}
            sx={{
              p: 3,
              display: 'flex',
              flexDirection: 'column',
              height: 180,
              borderRadius: 2,
              position: 'relative',
              overflow: 'hidden',
              background: (theme) => `linear-gradient(45deg, ${theme.palette.secondary.main} 0%, ${theme.palette.secondary.light} 100%)`,
              color: 'white'
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                top: -30,
                right: -20,
                opacity: 0.2,
                transform: 'rotate(15deg)'
              }}
            >
              <People sx={{ fontSize: 160 }} />
            </Box>
            <Box sx={{ textAlign: 'center', zIndex: 2 }}>
              <Typography variant="h2" component="div" fontWeight="bold">
                {/* Placeholder - would come from backend */}
                128
              </Typography>
              <Typography variant="h6" sx={{ mt: 1 }}>
                Active Users
              </Typography>
            </Box>
          </Paper>
        </Grid>
        
        <Grid item xs={12} md={4}>
          <Paper
            elevation={3}
            sx={{
              p: 3,
              display: 'flex',
              flexDirection: 'column',
              height: 180,
              borderRadius: 2,
              position: 'relative',
              overflow: 'hidden',
              background: (theme) => `linear-gradient(45deg, ${alpha(theme.palette.warning.main, 0.8)} 0%, ${theme.palette.warning.light} 100%)`,
              color: 'white'
            }}
           >
            <Box
              sx={{
                position: 'absolute',
                top: -30,
                right: -20,
                opacity: 0.2,
                transform: 'rotate(15deg)'
              }}
            >
              <TrendingUp sx={{ fontSize: 160 }} />
            </Box>
            <Box sx={{ textAlign: 'center', zIndex: 2 }}>
              <Typography variant="h2" component="div" fontWeight="bold">
                {/* Placeholder - would come from backend */}
                256
              </Typography>
              <Typography variant="h6" sx={{ mt: 1 }}>
                Weekly Interactions
              </Typography>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Meal Type Distribution */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} md={6}>
          <Paper
            elevation={3}
            sx={{
              p: 3,
              borderRadius: 2,
              height: '100%'
            }}
          >
            <Box display="flex" alignItems="center" mb={2}>
              <DonutLarge color="primary" fontSize="medium" sx={{ mr: 1 }} />
              <Typography variant="h5" component="h2" fontWeight="bold">
                Meals by Type
              </Typography>
            </Box>
            <Divider sx={{ mb: 2 }} />
            
            <List>
              {mealTypesWithPercentage.map(({ type, count, percentage }) => (
                <ListItem key={type} sx={{ px: 0 }}>
                  <ListItemIcon>
                    <Avatar
                      sx={{
                        bgcolor: getColorForType(type),
                        color: 'white',
                        width: 36,
                        height: 36
                      }}
                    >
                      {getMealTypeIcon(type)}
                    </Avatar>
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="subtitle1" fontWeight="medium" sx={{ textTransform: 'capitalize' }}>
                          {type}
                        </Typography>
                        <Box>
                          <Typography variant="body2" color="text.secondary" display="inline">
                            {count} meals
                          </Typography>
                          <Chip
                            label={`${percentage}%`}
                            size="small"
                            sx={{
                              ml: 1,
                              bgcolor: getColorForType(type),
                              color: 'white',
                              fontWeight: 'bold'
                            }}
                          />
                        </Box>
                      </Box>
                    }
                    secondary={
                      <Box mt={0.5}>
                        <LinearProgress
                          variant="determinate"
                          value={percentage}
                          sx={{
                            mt: 0.5,
                            height: 8,
                            borderRadius: 1,
                            bgcolor: alpha(getColorForType(type), 0.2),
                            '& .MuiLinearProgress-bar': {
                              bgcolor: getColorForType(type)
                            }
                          }}
                        />
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>
            
            <Box mt={2} display="flex" justifyContent="flex-end">
              <Button
                component={RouterLink}
                to="/admin/meals?filter=type"
                color="primary"
                endIcon={<BarChart />}
              >
                View Full Analysis
              </Button>
            </Box>
          </Paper>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Paper
            elevation={3}
            sx={{
              p: 3,
              borderRadius: 2,
              height: '100%'
            }}
          >
            <Box display="flex" alignItems="center" mb={2}>
              <PieChart color="secondary" fontSize="medium" sx={{ mr: 1 }} />
              <Typography variant="h5" component="h2" fontWeight="bold">
                Meals by Diet
              </Typography>
            </Box>
            <Divider sx={{ mb: 2 }} />
            
            <List>
              {dietTypesWithPercentage.map(({ diet, count, percentage }) => (
                <ListItem key={diet} sx={{ px: 0 }}>
                  <ListItemIcon>
                    <Avatar
                      sx={{
                        bgcolor: getColorForType(diet),
                        color: 'white',
                        width: 36,
                        height: 36
                      }}
                    >
                      {getDietIcon(diet)}
                    </Avatar>
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="subtitle1" fontWeight="medium" sx={{ textTransform: 'capitalize' }}>
                          {diet}
                        </Typography>
                        <Box>
                          <Typography variant="body2" color="text.secondary" display="inline">
                            {count} meals
                          </Typography>
                          <Chip
                            label={`${percentage}%`}
                            size="small"
                            sx={{
                              ml: 1,
                              bgcolor: getColorForType(diet),
                              color: 'white',
                              fontWeight: 'bold'
                            }}
                          />
                        </Box>
                      </Box>
                    }
                    secondary={
                      <Box mt={0.5}>
                        <LinearProgress
                          variant="determinate"
                          value={percentage}
                          sx={{
                            mt: 0.5,
                            height: 8,
                            borderRadius: 1,
                            bgcolor: alpha(getColorForType(diet), 0.2),
                            '& .MuiLinearProgress-bar': {
                              bgcolor: getColorForType(diet)
                            }
                          }}
                        />
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>
            
            <Box mt={2} display="flex" justifyContent="flex-end">
              <Button
                component={RouterLink}
                to="/admin/meals?filter=diet"
                color="secondary"
                endIcon={<BarChart />}
              >
                View Full Analysis
              </Button>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Quick Actions */}
      <Paper
        elevation={3}
        sx={{
          p: 3,
          borderRadius: 2,
          background: theme => alpha(theme.palette.background.default, 0.7),
          backdropFilter: 'blur(10px)'
        }}
      >
        <Box display="flex" alignItems="center" mb={2}>
          <Settings color="primary" fontSize="medium" sx={{ mr: 1 }} />
          <Typography variant="h5" component="h2" fontWeight="bold">
            Quick Actions
          </Typography>
        </Box>
        <Divider sx={{ mb: 3 }} />
        
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <Card
              elevation={2}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-5px)',
                  boxShadow: '0 10px 20px rgba(0,0,0,0.1)'
                }
              }}
            >
              <CardContent sx={{ flexGrow: 1 }}>
                <Box display="flex" alignItems="center" mb={1}>
                  <Avatar sx={{ bgcolor: 'primary.main', mr: 1.5 }}>
                    <Fastfood />
                  </Avatar>
                  <Typography variant="h6">
                    Meals
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Add, update or remove meal suggestions from your database.
                </Typography>
              </CardContent>
              <CardActions>
                <Button
                  component={RouterLink}
                  to="/admin/meals"
                  color="primary"
                  fullWidth
                >
                  Manage Meals
                </Button>
              </CardActions>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card
              elevation={2}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-5px)',
                  boxShadow: '0 10px 20px rgba(0,0,0,0.1)'
                }
              }}
            >
              <CardContent sx={{ flexGrow: 1 }}>
                <Box display="flex" alignItems="center" mb={1}>
                  <Avatar sx={{ bgcolor: 'secondary.main', mr: 1.5 }}>
                    <TuneOutlined />
                  </Avatar>
                  <Typography variant="h6">
                    Preferences
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Configure dietary preferences and meal type settings.
                </Typography>
              </CardContent>
              <CardActions>
                <Button
                  component={RouterLink}
                  to="/admin/preferences"
                  color="secondary"
                  fullWidth
                >
                  Manage Preferences
                </Button>
              </CardActions>
            </Card>
          </Grid>
          
          
          <Grid item xs={12} sm={6} md={3}>
            <Card
              elevation={2}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-5px)',
                  boxShadow: '0 10px 20px rgba(0,0,0,0.1)'
                }
              }}
            >
              <CardContent sx={{ flexGrow: 1 }}>
                <Box display="flex" alignItems="center" mb={1}>
                  <Avatar sx={{ bgcolor: 'success.main', mr: 1.5 }}>
                    <People />
                  </Avatar>
                  <Typography variant="h6">
                    Users
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  View and manage user accounts and subscriptions.
                </Typography>
              </CardContent>
              <CardActions>
                <Button
                  component={RouterLink}
                  to="/admin/users"
                  color="success"
                  fullWidth
                >
                  Manage Users
                </Button>
              </CardActions>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card
              elevation={2}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-5px)',
                  boxShadow: '0 10px 20px rgba(0,0,0,0.1)'
                }
              }}
            >
              <CardContent sx={{ flexGrow: 1 }}>
                <Box display="flex" alignItems="center" mb={1}>
                  <Avatar sx={{ bgcolor: 'info.main', mr: 1.5 }}>
                    <Add />
                  </Avatar>
                  <Typography variant="h6">
                    Add New Meal
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Create a new meal suggestion with detailed recipe.
                </Typography>
              </CardContent>
              <CardActions>
                <Button
                  component={RouterLink}
                  to="/admin/meals/new"
                  color="info"
                  fullWidth
                >
                  Create Meal
                </Button>
              </CardActions>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
          <Card
            elevation={2}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': {
                transform: 'translateY(-5px)',
                boxShadow: '0 10px 20px rgba(0,0,0,0.1)'
              }
            }}
          >
            <CardContent sx={{ flexGrow: 1 }}>
              <Box display="flex" alignItems="center" mb={1}>
                <Avatar sx={{ bgcolor: 'warning.main', mr: 1.5 }}>
                  <NotificationsActive />
                </Avatar>
                <Typography variant="h6">
                  Add Notification
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Send a new notification to users, instantly or schedule it.
              </Typography>
            </CardContent>
            <CardActions>
              <Button
                component={RouterLink}
                to="/admin/notifications"
                color="warning"
                fullWidth
              >
                Create Notification
              </Button>
            </CardActions>
          </Card>
        </Grid>
        </Grid>
      </Paper>
    </Container>
  );
};

export default Dashboard;
